import React from 'react';
import { EditIcon, DatabaseIcon, PackageIcon, CheckIcon, XIcon, DownloadIcon, BuildingIcon, HomeIcon } from 'lucide-react';
import { Catalog } from '../CatalogsManagement';
interface CatalogCardProps {
  catalog: Catalog;
  onEdit: () => void;
  onManageSchema: () => void;
  onManageProducts: () => void;
  onToggleStatus: () => void;
  locale: string;
}
export function CatalogCard({
  catalog,
  onEdit,
  onManageSchema,
  onManageProducts,
  onToggleStatus,
  locale
}: CatalogCardProps) {
  // Format date based on locale
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No End Date';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-shadow duration-200" data-id="element-13527">
      {/* Card Header */}
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-13528">
        <div className="flex justify-between items-start" data-id="element-13529">
          <div data-id="element-13530">
            <h3 className="text-lg leading-6 font-medium text-gray-900 truncate" title={catalog.name} data-id="element-13531">
              {catalog.name}
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 truncate" title={catalog.description} data-id="element-13532">
              {catalog.description}
            </p>
          </div>
          <div data-id="element-13533">
            {catalog.isActive ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-13534">
                <CheckIcon className="h-4 w-4 mr-1" data-id="element-13535" />
                Active
              </span> : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800" data-id="element-13536">
                <XIcon className="h-4 w-4 mr-1" data-id="element-13537" />
                Inactive
              </span>}
          </div>
        </div>
      </div>
      {/* Card Body */}
      <div className="px-4 py-5 sm:p-6" data-id="element-13538">
        <div className="grid grid-cols-2 gap-4" data-id="element-13539">
          <div data-id="element-13540">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13541">
              Vendor
            </h4>
            <p className="mt-1 text-sm text-gray-900" data-id="element-13542">{catalog.vendorName}</p>
          </div>
          <div data-id="element-13543">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13544">
              Products
            </h4>
            <p className="mt-1 text-sm text-gray-900" data-id="element-13545">{catalog.productCount}</p>
          </div>
          <div data-id="element-13546">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13547">
              Active From
            </h4>
            <p className="mt-1 text-sm text-gray-900" data-id="element-13548">
              {formatDate(catalog.activeFrom)}
            </p>
          </div>
          <div data-id="element-13549">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13550">
              Active To
            </h4>
            <p className="mt-1 text-sm text-gray-900" data-id="element-13551">
              {formatDate(catalog.activeTo)}
            </p>
          </div>
        </div>
        <div className="mt-4" data-id="element-13552">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13553">
            Scope
          </h4>
          <div className="mt-1 flex flex-wrap gap-2" data-id="element-13554">
            <div className="flex items-center text-xs text-gray-700" data-id="element-13555">
              <BuildingIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-13556" />
              {catalog.entities.length}{' '}
              {catalog.entities.length === 1 ? 'Entity' : 'Entities'}
            </div>
            <div className="flex items-center text-xs text-gray-700" data-id="element-13557">
              <HomeIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-13558" />
              {catalog.sites.length}{' '}
              {catalog.sites.length === 1 ? 'Site' : 'Sites'}
            </div>
          </div>
        </div>
      </div>
      {/* Card Footer */}
      <div className="px-4 py-4 sm:px-6 bg-gray-50 border-t border-gray-200" data-id="element-13559">
        <div className="flex justify-between" data-id="element-13560">
          <button onClick={onManageProducts} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13561">
            <PackageIcon className="h-4 w-4 mr-1" data-id="element-13562" />
            Products
          </button>
          <div className="flex space-x-2" data-id="element-13563">
            <button onClick={onEdit} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-indigo-600 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" title="Edit Catalog" data-id="element-13564">
              <EditIcon className="h-4 w-4" data-id="element-13565" />
            </button>
            <button onClick={onManageSchema} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-indigo-600 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" title="Manage Schema" data-id="element-13566">
              <DatabaseIcon className="h-4 w-4" data-id="element-13567" />
            </button>
            <button onClick={onToggleStatus} className={`inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${catalog.isActive ? 'text-red-600 bg-red-50 hover:bg-red-100 focus:ring-red-500' : 'text-green-600 bg-green-50 hover:bg-green-100 focus:ring-green-500'}`} title={catalog.isActive ? 'Deactivate' : 'Activate'} data-id="element-13568">
              {catalog.isActive ? <XIcon className="h-4 w-4" data-id="element-13569" /> : <CheckIcon className="h-4 w-4" data-id="element-13570" />}
            </button>
            <button className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-gray-600 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" title="Export Products" data-id="element-13571">
              <DownloadIcon className="h-4 w-4" data-id="element-13572" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}